import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Funnel, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface FilterButtonProps {
  /** How many filters differ from their default — shown as a badge on the button. */
  activeCount?: number
  /** Puts every filter back to its default. */
  onReset?: () => void
  /** `FilterField` entries holding the filter controls. */
  children: ReactNode
}

/**
 * Single button above a list that opens the list's filters in a popover, so
 * the toolbar stays clean no matter how many filters a list has.
 *
 * A filtered list says so from the toolbar rather than only from inside the
 * popover: the button takes the primary colour, and a clear button appears
 * beside it so getting back to the whole list is one click, not two.
 */
export function FilterButton({ activeCount = 0, onReset, children }: FilterButtonProps) {
  const { t } = useTranslation()
  const isFiltered = activeCount > 0

  return (
    <div className="flex items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('h-8', isFiltered && 'border-(--ad-primary) text-(--ad-primary)')}
          >
            <Funnel className="size-4" />
            {t('common.filters')}
            {isFiltered && <span className="font-medium">{activeCount}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <div className="border-b border-(--ad-border) px-3 py-2">
            <span className="text-sm font-medium">{t('common.filters')}</span>
          </div>
          <div className="flex flex-col gap-3 p-3">{children}</div>
        </PopoverContent>
      </Popover>

      {isFiltered && onReset && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-(--ad-text-muted)"
          title={t('common.resetFilters')}
          aria-label={t('common.resetFilters')}
          onClick={onReset}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}

interface FilterFieldProps {
  label: string
  children: ReactNode
}

/** One labelled filter control inside a `FilterButton` popover. */
export function FilterField({ label, children }: FilterFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-(--ad-text-muted)">{label}</Label>
      {children}
    </div>
  )
}
